import {CSSProperties, Dispatch, memo, SetStateAction} from 'react';

import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';

import {AppButton, ButtonContainer} from '@/app/shared-components';
import PasswordTextField from '@/app/shared-components/PasswordTextField/PasswordTextField';
import {IVerifyPassword} from '@/app/shared-components/VerifyPassword/models/interface';
import VerifyPassword from '@/app/shared-components/VerifyPassword/VerifyPassword';

interface SignUpStep3FormFragmentProps {
  password: string;
  confirmPassword: string;
  verifyList: IVerifyPassword[];
  isDisabledSignUpStep3: boolean;
  isLoadingAmplifyAuth: boolean;
  isMobile: boolean;
  setPassword: Dispatch<SetStateAction<string>>;
  setConfirmPassword: Dispatch<SetStateAction<string>>;
  setIsVerify: Dispatch<SetStateAction<boolean>>;
  handleSignUp: () => void;
}

const PASSWORD_FIELDS_ROW_STYLE: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  gap: 20,
};

export const SignUpStep3FormFragment = memo(
  ({
    password,
    confirmPassword,
    verifyList,
    isDisabledSignUpStep3,
    isLoadingAmplifyAuth,
    isMobile,
    setPassword,
    setConfirmPassword,
    setIsVerify,
    handleSignUp,
  }: SignUpStep3FormFragmentProps) => {
    const {t} = useTranslation();

    const passwordField = (
      <PasswordTextField
        label={t('sign_up.password')}
        password={password}
        setPassword={setPassword}
        id="SignUpPage_passwordInput"
        key={'password'}
        autocomplete={'password'}
      />
    );

    const confirmPasswordField = (
      <PasswordTextField
        label={t('sign_up.confirm_password')}
        password={confirmPassword}
        setPassword={setConfirmPassword}
        key={'confirm-password'}
        id="SignUpPage_confirmPasswordInput"
        autocomplete={'password'}
      />
    );

    const verifyPasswordList = (
      <VerifyPassword
        newPassword={password}
        confirmPassword={confirmPassword}
        verifyList={verifyList}
        setVerify={setIsVerify}
        hideVerifyListWithoutValue={true}
      />
    );

    return (
      <Box className="signup-form-container">
        <form className="signup-form">
          <Box className="password-container">
            {isMobile ? (
              // Mobile: requirements sit between the password and confirm fields
              <>
                {passwordField}
                {verifyPasswordList}
                {confirmPasswordField}
              </>
            ) : (
              // Desktop: fields side by side, requirements below
              <>
                <div style={PASSWORD_FIELDS_ROW_STYLE}>
                  {passwordField}
                  {confirmPasswordField}
                </div>
                {verifyPasswordList}
              </>
            )}
          </Box>
        </form>
        <ButtonContainer sticky>
          <AppButton
            id="SignUpPage_confirmButton"
            label={t('sign_up.sign_up_button')}
            onClick={handleSignUp}
            disabled={!!isDisabledSignUpStep3 || isLoadingAmplifyAuth}
            isLoading={isLoadingAmplifyAuth}
          />
        </ButtonContainer>
        {!isMobile && (
          <div className="signup-disclaimers">
            <p>{t('authentication.footer.based_on')}</p>
          </div>
        )}
      </Box>
    );
  },
);
